/** @format */
/* eslint react/prop-types: 1, no-else-return: 1 */

import React, {useState, useEffect} from 'react';
import {Box, Container} from '@chakra-ui/layout';
import Patterns from '../../patterns';
import Layout from '../../components/Layout';
import {getContentTypes} from '../../lib/api/contentTypes';
import {getTerms, getContentNode} from '../../lib/api/contentNodes';
import {getSeoFields} from '../../lib/getSeoFields';
import {NextSeo} from 'next-seo';
import getFooter from '../../lib/api/acf/footer';
import mappingFooter from '../../patterns/Footer/mappingFooter';
import getMainMenu from '../../lib/api/acf/mainMenu';
import mappingMainMenu from '../../patterns/MainMenu/mappingMainMenu';
import {ctaBannerMapping} from '../../patterns/CTABanners/ctaBannerMapping';
import CTABanner from '../../patterns/CTABanners';
import {titleTextBannerMapping} from '../../patterns/TitleTextBanner/titleTextBannerMapping';
import TitleTextBanner from '../../patterns/TitleTextBanner';
import WYSIWYG from '../../patterns/WYSIWYG';
import {wysiwygMapping} from '../../patterns/WYSIWYG/wysiwygMapping';
import Image from '../../patterns/Image';
import {imageMapping} from '../../patterns/Image/imageMapping';
import {colors} from '../../styles/js/colors';
import FadedContainer from '../../components/FadedContainer';

import {useAuth} from '../../providers/authProvider';
import {gql, useQuery} from '@apollo/client';
import contentData from '../../data/contentData';

const PreviewContent = data => {
	if (!data) {
		return;
	}

	const ctaProps = ctaBannerMapping(contentData.protectedContent.post);

	/* eslint @typescript-eslint/no-unsafe-call: 1, @typescript-eslint/no-unsafe-assignment: 1, array-callback-return: 1 */
	return (
		<Box pb={10}>
			<FadedContainer>
				{data?.data?.article?.pageLayout?.flexibleContent?.map(
					(pattern: any, index: number) => {
						switch (pattern.fieldGroupName) {
							case 'Article_Pagelayout_FlexibleContent_TitleTextBanner': {
								const patternData = titleTextBannerMapping(pattern);
								return <TitleTextBanner key={index} {...patternData} />;
							}

							case 'Article_Pagelayout_FlexibleContent_Image': {
								const patternData = imageMapping(pattern);
								return <Image key={index} {...patternData} />;
							}

							case 'Article_Pagelayout_FlexibleContent_Wysiwyg': {
								return <WYSIWYG key={index} html={pattern.wysiwyg} />;
							}

							default:
								break;
						}
					},
				)}
			</FadedContainer>

			<CTABanner {...ctaProps} />
		</Box>
	);
};

const FullContent = post => {
	if (!post) {
		return;
	}

	return (
		<Box mb={10}>
			{/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
			{post?.post?.pageLayout?.flexibleContent?.map(
				(post: any, index: number) => (
					<Patterns
						/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
						fieldGroupName={post.__typename}
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						post={post}
						// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
						key={`${post.fieldGroupName}_${index}`}
					/>
				),
			)}
		</Box>
	);
};

// eslint-disable-next-line react/prop-types
const Article = ({post, seo, mainMenu, footer}) => {
	if (!post) {
		return null;
	}

	const {user} = useAuth();

	const [isLoggedIn, setIsLoggedIn] = useState(false);

	// query for post title & post image
	const prePostQuery = gql`
		query {
			article(id: "${post.id}") {
				pageLayout {
					flexibleContent {
						... on Article_Pagelayout_FlexibleContent_TitleTextBanner {
							fieldGroupName
							includeBackButton
							pageTitle
							backButtonLink {
								url
							}
							includePublishedDate
						}
						... on Article_Pagelayout_FlexibleContent_Wysiwyg {
							fieldGroupName
							wysiwyg
						}
						... on Article_Pagelayout_FlexibleContent_Image {
							fieldGroupName
							image {
								altText
								sourceUrl
								mediaDetails {
									height
									width
								}
							}
						}
					}
				}
			}
		}
	`;
	const {loading, error, data} = useQuery(prePostQuery);

	// if the user is logged in, display the post content. else, display the cta banner
	const PostContent = () => {
		if (isLoggedIn) {
			return <FullContent post={post} />;
		}

		if (loading) {
			return (
				<Container maxW='container.xl' mx='auto'>
					<Box py={10}>Loading...</Box>
				</Container>
			);
		}

		if (error) {
			console.log(error.message);
			return (
				<Container maxW='container.xl' mx='auto'>
					<Box py={10}>
						The content you requested is not curently available.
					</Box>
				</Container>
			);
		}

		return <PreviewContent data={data} />;
	};

	useEffect(() => {
		if (user) {
			if (user?.isLoggedIn) {
				setIsLoggedIn(true);
			} else {
				setIsLoggedIn(false);
			}
		}
	}, [user, isLoggedIn]);

	return (
		/* eslint-disable-next-line react/prop-types, @typescript-eslint/no-unsafe-assignment */
		<Layout seo={post.seo} mainMenu={mainMenu} footer={footer}>
			<NextSeo {...seo} />

			<PostContent />
		</Layout>
	);
};

export async function getStaticPaths() {
	let contentTypes = await getContentTypes();
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
	contentTypes = contentTypes.filter(
		contentType => contentType.name === 'article',
	);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
	const uris = contentTypes
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		.map(contentType => contentType.contentNodes.nodes)
		.flat()
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		.map(({uri}) => uri);

	return {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		paths: uris,
		fallback: 'blocking',
	};
}

export async function getStaticProps({params, preview, previewData}) {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/restrict-template-expressions
	const id = preview ? previewData.id : `article/${params.slug}`;
	const idType = preview ? 'DATABASE_ID' : 'URI';

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const post = await getContentNode(id, idType, preview);
	// eslint-disable-next-line no-negated-condition
	const seo = !preview ? getSeoFields(post) : null;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const terms = await getTerms(id, idType, preview);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let mainMenu = await getMainMenu();
	mainMenu = mappingMainMenu(mainMenu);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let footer = await getFooter();
	footer = mappingFooter(footer);

	return {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		props: {post, seo, mainMenu, footer, terms: terms || null},
		revalidate: 60,
	};
}

export default Article;
